/**
 * Fetch-basierter API-Client für PayPilot.
 * - Bearer-Token aus oidc-client-ts (Memory)
 * - JSON default (snake_case auf dem Wire, camelCase im Frontend)
 * - UUIDs als Strings
 * - Fehler als AppError
 */
import { toast } from "sonner";
import { getAccessToken, redirectToHub } from "@/lib/auth";

export type ApiErrorPayload = {
  code?: string;
  message?: string;
  field?: string;
  // Note: by the time the payload reaches handlers/error consumers, snake_case
  // keys from the wire have already been transformed to camelCase. We accept
  // both shapes defensively because StepUp-Error parsing happens BEFORE the
  // generic transform pass for non-2xx responses.
  details?: Record<string, unknown>;
};

export class AppError extends Error {
  status: number;
  code?: string;
  payload?: ApiErrorPayload;

  constructor(status: number, message: string, payload?: ApiErrorPayload) {
    super(message);
    this.name = "AppError";
    this.status = status;
    this.code = payload?.code;
    this.payload = payload;
  }
}

/**
 * Step-Up-Required-Error.
 *
 * Wird vom API-Client geworfen, wenn das Backend mit
 *   { code: "STEPUP_REQUIRED", details: { challenge_id, redirect_url, action } }
 * antwortet. Hooks können diesen Fehler optional fangen, er wird aber bereits
 * vom API-Client global behandelt: wir zeigen einen Toast und leiten zur
 * `redirectUrl` um (mit `return_to` an die aktuelle Seite).
 */
export class StepUpRequiredError extends AppError {
  challengeId: string;
  redirectUrl: string;
  action: string;

  constructor(payload: ApiErrorPayload, redirectUrl: string, challengeId: string, action: string) {
    // Backend schreibt den Klartext ins `error`-Feld (siehe ErrorBody),
    // historisch unterstuetzen wir auch `message` als Fallback.
    const rawMsg =
      (payload as { error?: string }).error ??
      payload.message ??
      "step-up authentication required";
    super(401, rawMsg, payload);
    this.name = "StepUpRequiredError";
    this.challengeId = challengeId;
    this.redirectUrl = redirectUrl;
    this.action = action;
  }
}

/** Code-Konstanten — bewusst Strings, kein magic-string-vergleich in Hooks. */
export const STEPUP_REQUIRED_CODE = "STEPUP_REQUIRED";

/**
 * Liest aus einem Backend-Error-Body die Step-Up-Felder. Backend liefert
 * snake_case (`challenge_id`, `redirect_url`); falls eine Transformation den
 * Body schon nach camelCase umgeschrieben hat, akzeptieren wir auch die
 * Variante `challengeId`/`redirectUrl`. Gibt null zurueck, wenn der Body kein
 * Step-Up-Error ist.
 */
function parseStepUpPayload(payload: unknown): { challengeId: string; redirectUrl: string; action: string } | null {
  if (!payload || typeof payload !== "object") return null;
  const root = payload as Record<string, unknown>;
  const code = typeof root.code === "string" ? root.code : undefined;
  if (code !== STEPUP_REQUIRED_CODE) return null;

  const details = (root.details as Record<string, unknown> | undefined) ?? {};
  const challengeId =
    (typeof details.challenge_id === "string" ? details.challenge_id : undefined) ??
    (typeof details.challengeId === "string" ? details.challengeId : undefined) ??
    "";
  const redirectUrl =
    (typeof details.redirect_url === "string" ? details.redirect_url : undefined) ??
    (typeof details.redirectUrl === "string" ? details.redirectUrl : undefined) ??
    "";
  const action = typeof details.action === "string" ? details.action : "";
  if (!redirectUrl) return null;
  return { challengeId, redirectUrl, action };
}

/**
 * Haengt einen `return_to`-Parameter an die Step-Up-URL an, sofern noch nicht
 * vorhanden. Damit kommt der Browser nach erfolgreicher Step-Up-Bestaetigung
 * automatisch zur urspruenglichen PayPilot-Seite zurueck.
 */
function withReturnTo(redirectUrl: string, returnTo: string): string {
  try {
    const u = new URL(redirectUrl, window.location.origin);
    if (!u.searchParams.has("return_to")) {
      u.searchParams.set("return_to", returnTo);
    }
    return u.toString();
  } catch {
    // Falls die URL nicht parseable ist (z.B. relativ ohne Origin), unveraendert
    // zurueckgeben — Toast/Redirect funktioniert trotzdem.
    return redirectUrl;
  }
}

/**
 * Globaler Fallback-Handler für StepUpRequiredError. Zeigt einen Toast,
 * wartet 1.5s und leitet dann zur Step-Up-Seite um.
 */
let stepUpRedirectScheduled = false;
export function showStepUpToast(redirectUrl: string): void {
  // Doppelausloesung verhindern, falls mehrere Mutations gleichzeitig 401en.
  if (stepUpRedirectScheduled) return;
  stepUpRedirectScheduled = true;

  const fullUrl = withReturnTo(redirectUrl, window.location.href);
  toast.warning("Diese Aktion benötigt eine zusätzliche Sicherheitsbestätigung", {
    description: "Du wirst gleich zur Bestätigungsseite weitergeleitet.",
    duration: 1500,
  });
  window.setTimeout(() => {
    window.location.href = fullUrl;
  }, 1500);
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  raw?: boolean;
  /** Wenn true, wird der Body NICHT von camelCase → snake_case transformiert */
  skipBodyTransform?: boolean;
  /** Wenn true, wird die Response NICHT von snake_case → camelCase transformiert */
  skipResponseTransform?: boolean;
}

/**
 * Baut einen workspace-scoped API-v1-Pfad zusammen.
 * Beispiel: apiV1WsPath("ws-123", "invoices") → "/api/v1/workspaces/ws-123/invoices"
 * Segment darf führende Slashes haben, sie werden ignoriert.
 */
export function apiV1WsPath(workspaceId: string, segment: string): string {
  const seg = segment.replace(/^\/+/, "");
  return `/api/v1/workspaces/${workspaceId}${seg ? "/" + seg : ""}`;
}

// ---------------------------------------------------------------------------
// snake_case <-> camelCase transforms
// ---------------------------------------------------------------------------

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;

function snakeToCamel(s: string): string {
  // ASCII fast path: only transform keys consisting of lower-case letters, digits, underscores.
  // Leading/trailing underscores or keys that look like UUIDs/dates etc. stay untouched.
  if (!s || s.startsWith("_") || s.endsWith("_")) return s;
  if (!/^[a-z0-9_]+$/.test(s)) return s;
  if (!s.includes("_")) return s;
  return s.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());
}

function camelToSnake(s: string): string {
  if (!s || s.startsWith("_") || s.endsWith("_")) return s;
  // Detect camelCase signal: at least one upper-case letter.
  if (!/[A-Z]/.test(s)) return s;
  return s.replace(/([A-Z])/g, (_, c: string) => "_" + c.toLowerCase());
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  if (v === null || typeof v !== "object") return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

function shouldSkipValue(v: unknown): boolean {
  // Strings that look like UUIDs/ISO-dates are values — never transform values, only keys.
  // This helper is unused right now but documents intent: our transform NEVER touches values,
  // only keys. UUIDs/dates as values pass through unchanged by definition.
  return typeof v === "string" && (UUID_RE.test(v) || ISO_DATE_RE.test(v));
}

export function toCamelCase<T = unknown>(value: unknown): T {
  if (Array.isArray(value)) {
    return value.map((v) => toCamelCase(v)) as unknown as T;
  }
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(value)) {
      const newKey = snakeToCamel(k);
      out[newKey] = toCamelCase((value as Record<string, unknown>)[k]);
    }
    return out as T;
  }
  return value as T;
}

export function toSnakeCase(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((v) => toSnakeCase(v));
  }
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(value)) {
      const newKey = camelToSnake(k);
      out[newKey] = toSnakeCase((value as Record<string, unknown>)[k]);
    }
    return out;
  }
  return value;
}

// Silence unused warning — helper is documented API surface for future use.
void shouldSkipValue;

// ---------------------------------------------------------------------------

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const base = path.startsWith("/") ? path : "/" + path;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    // Query-Keys auch snake_case auf dem Wire
    params.set(camelToSnake(k), String(v));
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path, opts.query);
  const headers = new Headers(opts.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");

  // Bearer-Token aus oidc-client-ts (Memory).
  if (!headers.has("Authorization")) {
    const token = await getAccessToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    } else {
      // Kein Token → Sitzung abgelaufen. Sofort zum Hub redirecten,
      // Keycloak-SSO-Session reicht meist für einen nahtlosen Re-Login.
      redirectToHub(window.location.href);
      throw new AppError(401, "Sitzung abgelaufen – bitte erneut anmelden.");
    }
  }
  const hasBody = opts.body !== undefined && opts.body !== null;
  let body: BodyInit | undefined;
  if (hasBody) {
    if (opts.body instanceof FormData) {
      body = opts.body;
    } else if (opts.body instanceof Blob || opts.body instanceof ArrayBuffer) {
      body = opts.body as BodyInit;
    } else if (typeof opts.body === "string") {
      if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
      body = opts.body;
    } else {
      if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
      const shaped = opts.skipBodyTransform ? opts.body : toSnakeCase(opts.body);
      body = JSON.stringify(shaped);
    }
  }

  let res: Response;
  try {
    const {
      body: _ignored,
      headers: _ignored2,
      query: _ignored3,
      raw: _ignored4,
      skipBodyTransform: _ignored5,
      skipResponseTransform: _ignored6,
      ...rest
    } = opts;
    void _ignored;
    void _ignored2;
    void _ignored3;
    void _ignored4;
    void _ignored5;
    void _ignored6;
    res = await fetch(url, {
      method,
      headers,
      body,
      credentials: "include",
      ...rest,
    });
  } catch (err) {
    throw new AppError(0, "Netzwerkfehler: " + String((err as Error).message));
  }

  if (opts.raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;

  const ct = res.headers.get("content-type") || "";
  const isJson = ct.includes("application/json");
  const rawPayload = isJson ? await res.json().catch(() => null) : await res.text().catch(() => "");

  if (!res.ok) {
    if (res.status === 401) {
      // Step-Up vor Hub-Redirect prüfen — sonst würde der User durch den
      // Login-Flow geschickt obwohl er nur eine MFA-Bestätigung machen muss.
      if (isJson) {
        const stepUp = parseStepUpPayload(rawPayload);
        if (stepUp) {
          const errPayload = rawPayload as ApiErrorPayload;
          showStepUpToast(stepUp.redirectUrl);
          throw new StepUpRequiredError(
            errPayload ?? {},
            withReturnTo(stepUp.redirectUrl, window.location.href),
            stepUp.challengeId,
            stepUp.action,
          );
        }
      }
      // Token ungültig oder abgelaufen (Server-seitig) → Re-Auth via Hub.
      redirectToHub(window.location.href);
      throw new AppError(401, "Sitzung abgelaufen – bitte erneut anmelden.");
    }
    const errPayload = isJson ? (rawPayload as ApiErrorPayload) : undefined;
    const msg =
      (isJson && errPayload?.message) ||
      (typeof rawPayload === "string" ? rawPayload : res.statusText) ||
      `HTTP ${res.status}`;
    throw new AppError(res.status, msg, errPayload);
  }

  if (isJson && !opts.skipResponseTransform) {
    const camel = toCamelCase<unknown>(rawPayload);
    // Der Backend-Envelope ist `{ data: ..., meta?: ... }`. Wenn der Response
    // genau diese Shape hat, packen wir ihn für den Aufrufer transparent aus.
    // Achtung: nur entpacken, wenn es wirklich wie ein Envelope aussieht — sonst
    // fressen wir echte Payloads, die zufaellig `data` enthalten.
    if (
      camel &&
      typeof camel === "object" &&
      !Array.isArray(camel) &&
      "data" in (camel as Record<string, unknown>) &&
      Object.keys(camel as Record<string, unknown>).every((k) => k === "data" || k === "meta" || k === "error")
    ) {
      return (camel as { data: T }).data as T;
    }
    return camel as T;
  }
  return rawPayload as T;
}

export const apiClient = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>("GET", path, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>("POST", path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>("PUT", path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>("PATCH", path, { ...opts, body }),
  delete: <T>(path: string, opts?: RequestOptions) => request<T>("DELETE", path, opts),
};
